import jsPDF from "jspdf";
import autoTable, { type CellHookData } from "jspdf-autotable";
import { calculateLineItemTax, getGstTaxRowCount } from "./billingPeriodUtils";
import type {
  BankDetails,
  InvoiceAddressBlock,
  InvoicePreviewLineItem,
} from "./types";

const PAGE_MARGIN = 15;
const CONTENT_WIDTH = 180;
const TEXT_BLACK: [number, number, number] = [0, 0, 0];
const TEXT_MUTED: [number, number, number] = [100, 100, 100];
const BORDER_COLOR_GRAY: [number, number, number] = [180, 180, 180];
const TABLE_HEADER_FILL: [number, number, number] = [245, 245, 245];

const TABLE_COLUMNS = {
  index: 10,
  description: 68,
  hsnSac: 24,
  qty: 16,
  rate: 30,
  amount: 32,
} as const;

const TOTALS_TABLE_WIDTH = 72;
const ADDRESS_BOX_MIN_HEIGHT = 34;
const ADDRESS_BOX_GAP = 6;
const ADDRESS_BOX_PADDING_X = 4;
const ADDRESS_BOX_BOTTOM_PADDING = 4;
const ADDRESS_LABEL_OFFSET_Y = 6;
const ADDRESS_NAME_OFFSET_Y = 5;
const ADDRESS_LINE_HEIGHT = 4.5;
const ADDRESS_SECTION_BOTTOM_GAP = 8;
const FOOTER_BLOCK_HEIGHT = 10;
const SUMMARY_GAP = 6;
/** Match InvoicePreviewPaymentSection `pt-32` (8rem ≈ 33.87mm) */
const PAYMENT_SECTION_TOP_GAP_MM = 8 * 4.233;

const FOOTER_DISCLAIMER =
  "This is a computer-generated invoice and does not require a physical signature";

export type { InvoiceAddressBlock };

export interface InvoicePdfData {
  logoSrc: string;
  invoiceTitle: string;
  invoiceNumber: string;
  invoiceDate: string;
  from: InvoiceAddressBlock;
  billTo: InvoiceAddressBlock;
  lineItems: InvoicePreviewLineItem[];
  totals: {
    subtotal: number;
    taxTotal: number;
    total: number;
  };
  dueDays: number;
  bankDetails: BankDetails;
  gstType?: string;
}

function formatIndianNumber(value: number): string {
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatRateLabel(rate: number): string {
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(1);
}

function setTextStyle(
  doc: jsPDF,
  {
    bold = false,
    fontSize = 9,
  }: {
    bold?: boolean;
    fontSize?: number;
  } = {}
) {
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...TEXT_BLACK);
}

const LOGO_MAX_WIDTH_MM = 55;
const LOGO_MAX_HEIGHT_MM = 14;

interface LoadedImageAsset {
  dataUrl: string;
  width: number;
  height: number;
}

function fitImageSizeMm(
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number
) {
  const aspectRatio = naturalWidth / naturalHeight;
  let width = maxWidth;
  let height = width / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return { width, height };
}

async function loadImageAsset(src: string): Promise<LoadedImageAsset | null> {
  return new Promise(resolve => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(null);
        return;
      }
      context.drawImage(image, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

interface AddressBlockLayout {
  label: string;
  nameLines: string[];
  bodyLines: string[];
  height: number;
}

/** Splits on explicit newlines, then wraps each part to the box inner width */
function wrapAddressText(
  doc: jsPDF,
  value: string,
  maxWidth: number
): string[] {
  return value
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .flatMap(part => doc.splitTextToSize(part, maxWidth) as string[]);
}

function measureAddressBlock(
  doc: jsPDF,
  label: string,
  address: InvoiceAddressBlock,
  width: number
): AddressBlockLayout {
  const maxTextWidth = width - ADDRESS_BOX_PADDING_X * 2;

  setTextStyle(doc, { bold: true, fontSize: 8 });
  const nameLines = wrapAddressText(doc, address.name ?? "", maxTextWidth);

  setTextStyle(doc, { fontSize: 8 });
  const bodyLines = address.lines.flatMap(line =>
    wrapAddressText(doc, line ?? "", maxTextWidth)
  );

  if (address.gstin?.trim()) {
    bodyLines.push(
      ...wrapAddressText(doc, `GST NO : ${address.gstin.trim()}`, maxTextWidth)
    );
  }

  const extraLineCount = Math.max(nameLines.length, 1) - 1 + bodyLines.length;
  const contentHeight =
    ADDRESS_LABEL_OFFSET_Y +
    ADDRESS_NAME_OFFSET_Y +
    extraLineCount * ADDRESS_LINE_HEIGHT +
    ADDRESS_BOX_BOTTOM_PADDING;

  return {
    label,
    nameLines,
    bodyLines,
    height: Math.max(ADDRESS_BOX_MIN_HEIGHT, contentHeight),
  };
}

function drawAddressBlock(
  doc: jsPDF,
  layout: AddressBlockLayout,
  x: number,
  y: number,
  width: number,
  boxHeight: number
) {
  doc.setDrawColor(...BORDER_COLOR_GRAY);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, width, boxHeight, 2, 2, "S");

  const textX = x + ADDRESS_BOX_PADDING_X;
  let cursorY = y + ADDRESS_LABEL_OFFSET_Y;

  setTextStyle(doc, { bold: true, fontSize: 8 });
  doc.text(layout.label, textX, cursorY);

  cursorY += ADDRESS_NAME_OFFSET_Y;
  layout.nameLines.forEach((line, index) => {
    if (index > 0) {
      cursorY += ADDRESS_LINE_HEIGHT;
    }
    doc.text(line, textX, cursorY);
  });

  setTextStyle(doc, { fontSize: 8 });
  layout.bodyLines.forEach(line => {
    cursorY += ADDRESS_LINE_HEIGHT;
    doc.text(line, textX, cursorY);
  });
}

function getCellContent(raw: unknown): string {
  if (typeof raw === "string") {
    return raw;
  }

  if (raw && typeof raw === "object" && "content" in raw) {
    return String((raw as { content: unknown }).content ?? "");
  }

  return "";
}

function isLineItemMainRow(hookData: CellHookData): boolean {
  if (hookData.section !== "body") {
    return false;
  }

  const indexCell = hookData.row.cells[0];
  if (!indexCell) {
    return false;
  }

  return /^\d+$/.test(getCellContent(indexCell.raw).trim());
}

function getDescriptionMinHeight(item: InvoicePreviewLineItem): number {
  return item.subtitle?.trim() || item.billingPeriod?.trim() ? 14 : 8;
}

function styleDescriptionCell(hookData: CellHookData) {
  if (hookData.section !== "body" || hookData.column.index !== 1) {
    return;
  }

  if (!isLineItemMainRow(hookData)) {
    return;
  }

  hookData.cell.text = [];
}

function drawLineItemDescription(
  doc: jsPDF,
  cell: CellHookData["cell"],
  item: InvoicePreviewLineItem
) {
  const { x, y } = cell;
  const padding = 2.5;
  let currentY = y + padding + 3;

  setTextStyle(doc, { bold: true, fontSize: 9 });
  doc.text(item.title, x + padding, currentY);

  if (!item.subtitle?.trim() && !item.billingPeriod?.trim()) {
    return;
  }

  currentY += 4.2;

  // Format: (01 Jul 2026 – 31 Jul 2026) · Monthly
  if (item.billingPeriod?.trim()) {
    setTextStyle(doc, { fontSize: 8 });
    doc.text(`(${item.billingPeriod})`, x + padding, currentY);
  }

  if (item.subtitle?.trim()) {
    const frequencyX = item.billingPeriod?.trim()
      ? x + padding + doc.getTextWidth(`(${item.billingPeriod})`)
      : x + padding;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(
      item.billingPeriod?.trim() ? ` · ${item.subtitle}` : item.subtitle,
      frequencyX,
      currentY
    );
  }
}

function getPageHeight(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight();
}

function getContentBottomY(doc: jsPDF): number {
  return getPageHeight(doc) - PAGE_MARGIN;
}

function estimateSummaryHeight(totals: InvoicePdfData["totals"]): number {
  const totalsRowCount = totals.taxTotal > 0 ? 3 : 2;
  return totalsRowCount * 7 + PAYMENT_SECTION_TOP_GAP_MM + 40;
}

function ensureSummaryFitsOnPage(
  doc: jsPDF,
  startY: number,
  summaryHeight: number
): number {
  if (startY + summaryHeight > getContentBottomY(doc)) {
    doc.addPage();
    return PAGE_MARGIN;
  }

  return startY;
}

function getLastAutoTableFinalY(doc: jsPDF, fallbackY: number): number {
  return (
    (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? fallbackY
  );
}

function drawPageFooter(doc: jsPDF, contentEndY: number) {
  const pageCount = doc.getNumberOfPages();
  doc.setPage(pageCount);

  const pageHeight = getPageHeight(doc);
  const pinnedDisclaimerY = pageHeight - PAGE_MARGIN;
  const contentBottomY = getContentBottomY(doc);

  let disclaimerY = pinnedDisclaimerY;

  if (contentEndY + FOOTER_BLOCK_HEIGHT + 8 > contentBottomY) {
    doc.addPage();
    disclaimerY = PAGE_MARGIN + 4;
  } else if (contentEndY + 12 < pinnedDisclaimerY) {
    disclaimerY = pinnedDisclaimerY;
  } else {
    disclaimerY = contentEndY + 10;
  }

  const contentCenterX = PAGE_MARGIN + CONTENT_WIDTH / 2;

  setTextStyle(doc, { fontSize: 8 });
  doc.text(FOOTER_DISCLAIMER, contentCenterX, disclaimerY, {
    align: "center",
  });
}

function buildLineItemsTableBody(
  lineItems: InvoicePreviewLineItem[],
  gstType?: string
): Array<
  | string
  | {
      content: string;
      rowSpan?: number;
      colSpan?: number;
      styles?: Record<string, unknown>;
    }
>[] {
  type TableCell =
    | string
    | {
        content: string;
        rowSpan?: number;
        colSpan?: number;
        styles?: Record<string, unknown>;
      };

  const tableBody: TableCell[][] = [];

  lineItems.forEach((item, index) => {
    const tax = calculateLineItemTax(
      item.amount,
      item.gstApplicable,
      item.gstRate,
      gstType
    );
    const taxRowCount = getGstTaxRowCount(item.gstApplicable, gstType);
    const rowSpan = item.gstApplicable ? taxRowCount + 1 : 1;

    tableBody.push([
      { content: String(index + 1), rowSpan, styles: { valign: "top" } },
      {
        content: item.title,
        styles: { minCellHeight: getDescriptionMinHeight(item) },
      },
      {
        content: item.hsnSac,
        rowSpan,
        styles: { valign: "top", textColor: TEXT_BLACK },
      },
      {
        content: String(item.qty),
        rowSpan,
        styles: { halign: "right", valign: "top" },
      },
      {
        content: formatIndianNumber(item.rate),
        rowSpan,
        styles: { halign: "right", valign: "top" },
      },
      {
        content: formatIndianNumber(item.amount),
        styles: { halign: "right" },
      },
    ]);

    if (!item.gstApplicable) {
      return;
    }

    const taxLabelStyles = {
      halign: "left",
      fontSize: 8,
      textColor: TEXT_MUTED,
      cellPadding: { top: 1.2, right: 2.5, bottom: 1.2, left: 2.5 },
    };
    const taxAmountStyles = {
      halign: "right",
      fontSize: 8,
      textColor: TEXT_BLACK,
      cellPadding: { top: 1.2, right: 2.5, bottom: 1.2, left: 2.5 },
    };

    if (tax.isInstate) {
      tableBody.push([
        {
          content: `CGST ${formatRateLabel(tax.halfGstRate)}%`,
          colSpan: 4,
          styles: taxLabelStyles,
        },
        {
          content: formatIndianNumber(tax.cgst),
          styles: taxAmountStyles,
        },
      ]);

      tableBody.push([
        {
          content: `SGST ${formatRateLabel(tax.halfGstRate)}%`,
          colSpan: 4,
          styles: taxLabelStyles,
        },
        {
          content: formatIndianNumber(tax.sgst),
          styles: taxAmountStyles,
        },
      ]);
      return;
    }

    tableBody.push([
      {
        content: `IGST ${formatRateLabel(tax.fullGstRate)}%`,
        colSpan: 4,
        styles: taxLabelStyles,
      },
      {
        content: formatIndianNumber(tax.igst),
        styles: taxAmountStyles,
      },
    ]);
  });

  return tableBody;
}

function drawLineItemsTable(
  doc: jsPDF,
  startY: number,
  lineItems: InvoicePreviewLineItem[],
  gstType?: string
) {
  const tableBody = buildLineItemsTableBody(lineItems, gstType);
  let lineItemDrawIndex = 0;

  autoTable(doc, {
    startY,
    margin: {
      left: PAGE_MARGIN,
      right: PAGE_MARGIN,
      top: PAGE_MARGIN,
      bottom: PAGE_MARGIN,
    },
    tableWidth: CONTENT_WIDTH,
    head: [["#", "Description", "HSN/SAC", "Qty", "Rate", "Amount"]],
    body: tableBody,
    theme: "plain",
    showHead: "everyPage",
    styles: {
      font: "helvetica",
      fontSize: 9,
      textColor: TEXT_BLACK,
      cellPadding: 2.5,
      lineColor: BORDER_COLOR_GRAY,
      lineWidth: 0,
      valign: "top",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: TABLE_HEADER_FILL,
      textColor: TEXT_BLACK,
      fontStyle: "bold",
      halign: "left",
      lineWidth: { bottom: 0.3 },
      lineColor: BORDER_COLOR_GRAY,
      cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
    },
    columnStyles: {
      0: { cellWidth: TABLE_COLUMNS.index, halign: "left" },
      1: { cellWidth: TABLE_COLUMNS.description, halign: "left" },
      2: { cellWidth: TABLE_COLUMNS.hsnSac, halign: "left" },
      3: { cellWidth: TABLE_COLUMNS.qty, halign: "right" },
      4: { cellWidth: TABLE_COLUMNS.rate, halign: "right" },
      5: { cellWidth: TABLE_COLUMNS.amount, halign: "right" },
    },
    didParseCell: hookData => {
      styleDescriptionCell(hookData);

      if (hookData.section === "head") {
        if (hookData.column.index >= 3) {
          hookData.cell.styles.halign = "right";
        }
      }

      if (hookData.section === "body") {
        hookData.cell.styles.lineWidth = {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        };
      }
    },
    didDrawCell: hookData => {
      if (hookData.section !== "body" || hookData.column.index !== 1) {
        return;
      }

      if (!isLineItemMainRow(hookData)) {
        return;
      }

      const item = lineItems[lineItemDrawIndex];
      lineItemDrawIndex += 1;

      if (!item) {
        return;
      }

      drawLineItemDescription(hookData.doc, hookData.cell, item);
    },
  });
}

function drawTotalsSection(
  doc: jsPDF,
  startY: number,
  totals: InvoicePdfData["totals"]
): number {
  const totalsRows: string[][] = [
    ["Sub Total", formatIndianNumber(totals.subtotal)],
  ];

  if (totals.taxTotal > 0) {
    totalsRows.push(["Tax Total", formatIndianNumber(totals.taxTotal)]);
  }

  totalsRows.push(["Total", formatIndianNumber(totals.total)]);

  autoTable(doc, {
    startY,
    margin: { left: PAGE_MARGIN + CONTENT_WIDTH - TOTALS_TABLE_WIDTH },
    tableWidth: TOTALS_TABLE_WIDTH,
    body: totalsRows,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 9,
      textColor: TEXT_BLACK,
      cellPadding: { top: 1.8, right: 0, bottom: 1.8, left: 0 },
    },
    columnStyles: {
      0: { cellWidth: TOTALS_TABLE_WIDTH / 2, halign: "left" },
      1: { cellWidth: TOTALS_TABLE_WIDTH / 2, halign: "right" },
    },
    didParseCell: hookData => {
      const isTotalRow = hookData.row.index === totalsRows.length - 1;

      if (isTotalRow) {
        hookData.cell.styles.fontStyle = "bold";
        hookData.cell.styles.fontSize = 10;
        hookData.cell.styles.cellPadding = {
          top: 3,
          right: 0,
          bottom: 1.8,
          left: 0,
        };
        hookData.cell.styles.lineWidth = {
          top: 0.3,
          right: 0,
          bottom: 0,
          left: 0,
        };
      }
    },
  });

  return getLastAutoTableFinalY(doc, startY + 20);
}

function drawPaymentAndBankDetails(
  doc: jsPDF,
  startY: number,
  dueDays: number,
  bankDetails: BankDetails
): number {
  let cursorY = startY + PAYMENT_SECTION_TOP_GAP_MM;

  setTextStyle(doc, { bold: true, fontSize: 9 });
  doc.text("Payment Terms", PAGE_MARGIN, cursorY);
  setTextStyle(doc, { fontSize: 9 });
  doc.text(`Due within ${dueDays} days.`, PAGE_MARGIN, cursorY + 5);

  cursorY += 12;
  setTextStyle(doc, { bold: true, fontSize: 9 });
  doc.text("Bank Details", PAGE_MARGIN, cursorY);

  setTextStyle(doc, { fontSize: 9 });
  const bankRows: [string, string][] = [
    ["Bank Name", bankDetails.bankName],
    ["Account Name", bankDetails.accountName],
    ["Account Number", bankDetails.accountNumber],
    ["Branch Name", bankDetails.branchName],
    ["IFSC Code", bankDetails.ifscCode],
  ];

  const maxLabelWidth = Math.max(
    ...bankRows.map(([label]) => doc.getTextWidth(label))
  );
  const colonX = PAGE_MARGIN + maxLabelWidth + 1.5;
  const valueX = colonX + doc.getTextWidth(": ") + 0.5;

  bankRows.forEach(([label, value]) => {
    cursorY += 5;
    doc.text(label, PAGE_MARGIN, cursorY);
    doc.text(":", colonX, cursorY);
    doc.text(value, valueX, cursorY);
  });

  return cursorY;
}

export async function buildInvoicePdfDocument(
  data: InvoicePdfData
): Promise<jsPDF> {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  let y = PAGE_MARGIN;
  let headerHeight = 24;

  const logoAsset = await loadImageAsset(data.logoSrc);
  if (logoAsset) {
    const logoSize = fitImageSizeMm(
      logoAsset.width,
      logoAsset.height,
      LOGO_MAX_WIDTH_MM,
      LOGO_MAX_HEIGHT_MM
    );
    doc.addImage(
      logoAsset.dataUrl,
      "PNG",
      PAGE_MARGIN,
      y,
      logoSize.width,
      logoSize.height
    );
    headerHeight = Math.max(24, logoSize.height + 8);
  }

  setTextStyle(doc, { bold: true, fontSize: 13 });
  doc.text(data.invoiceTitle, PAGE_MARGIN + CONTENT_WIDTH, y + 4, {
    align: "right",
  });

  y += headerHeight;

  const metaColWidth = CONTENT_WIDTH / 2;
  const metaItems = [
    { label: "Invoice No.", value: data.invoiceNumber },
    { label: "Date", value: data.invoiceDate },
  ];

  metaItems.forEach((item, index) => {
    const x = PAGE_MARGIN + index * metaColWidth;
    setTextStyle(doc, { fontSize: 8 });
    doc.text(item.label, x, y);
    setTextStyle(doc, { bold: true, fontSize: 9 });
    doc.text(item.value, x, y + 5);
  });

  y += 14;

  const addressWidth = (CONTENT_WIDTH - ADDRESS_BOX_GAP) / 2;
  const fromLayout = measureAddressBlock(doc, "From", data.from, addressWidth);
  const billToLayout = measureAddressBlock(
    doc,
    "To",
    data.billTo,
    addressWidth
  );
  const addressBoxHeight = Math.max(fromLayout.height, billToLayout.height);

  drawAddressBlock(
    doc,
    fromLayout,
    PAGE_MARGIN,
    y,
    addressWidth,
    addressBoxHeight
  );
  drawAddressBlock(
    doc,
    billToLayout,
    PAGE_MARGIN + addressWidth + ADDRESS_BOX_GAP,
    y,
    addressWidth,
    addressBoxHeight
  );

  y += addressBoxHeight + ADDRESS_SECTION_BOTTOM_GAP;

  drawLineItemsTable(doc, y, data.lineItems, data.gstType);

  const summaryHeight = estimateSummaryHeight(data.totals);
  let summaryStartY = getLastAutoTableFinalY(doc, y + 20) + SUMMARY_GAP;
  summaryStartY = ensureSummaryFitsOnPage(doc, summaryStartY, summaryHeight);

  const totalsEndY = drawTotalsSection(doc, summaryStartY, data.totals);
  const contentEndY = drawPaymentAndBankDetails(
    doc,
    totalsEndY,
    data.dueDays,
    data.bankDetails
  );

  drawPageFooter(doc, contentEndY);

  return doc;
}

export async function downloadInvoicePdf(data: InvoicePdfData): Promise<void> {
  const doc = await buildInvoicePdfDocument(data);
  const safeFileName = data.invoiceNumber.replace(/[\\/]/g, "-") || "invoice";
  doc.save(`Invoice_${safeFileName}.pdf`);
}

export async function printInvoicePdf(data: InvoicePdfData): Promise<void> {
  const doc = await buildInvoicePdfDocument(data);
  doc.autoPrint({ variant: "non-conform" });

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    URL.revokeObjectURL(url);
    throw new Error("Please allow popups to print the invoice");
  }

  printWindow.document.open();
  printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Print Invoice</title>
    <style>
      html, body {
        margin: 0;
        width: 100%;
        height: 100%;
        background: #525659;
      }
      embed {
        display: block;
        width: 100%;
        height: 100%;
      }
    </style>
  </head>
  <body>
    <embed id="invoice-pdf" type="application/pdf" src="${url}" />
    <script>
      let hasPrinted = false;

      function triggerPrint() {
        if (hasPrinted) {
          return;
        }
        hasPrinted = true;
        window.focus();
        window.print();
      }

      const pdf = document.getElementById("invoice-pdf");
      pdf.addEventListener("load", function () {
        window.setTimeout(triggerPrint, 700);
      });

      window.addEventListener("afterprint", function () {
        window.setTimeout(function () {
          window.close();
        }, 200);
      });

      window.setTimeout(triggerPrint, 2500);
    </script>
  </body>
</html>`);
  printWindow.document.close();

  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export function formatInvoiceDateForPdf(value: string): string {
  if (!value) {
    return "—";
  }

  const [year, month, day] = value.split("-");
  if (!year || !month || !day) {
    return value;
  }

  return `${day}-${month}-${year}`;
}
